// Thiết lập giá (Mint-PriceSetup): tìm trong danh mục, quy đổi tỷ giá VCB, thứ tự tầng margin.
import type { MarginRule, RoeRow } from './types';

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();

/** Dòng khớp khi mọi từ của `q` (không dấu, không phân biệt hoa thường) nằm trong ghép các trường chữ. */
export function matchRow(fields: readonly (string | number | null | undefined)[], q: string): boolean {
  const terms = fold(q).split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = fold(fields.filter((f) => f !== null && f !== undefined).join(' '));
  return terms.every((t) => hay.includes(t));
}

/** Tỷ giá mới nhất của một loại tiền (theo CodeROE lớn nhất). */
export function latestRoe(rows: readonly RoeRow[], cur: string): RoeRow | undefined {
  return rows.filter((r) => r.cur === cur).sort((a, b) => b.code.localeCompare(a.code))[0];
}

/** "Hỏi Hana quy đổi": `100 usd`, `2.500 eur`, `1.000.000 vnd` → số tiền quy đổi theo VCB bán; không hiểu thì `null`. */
export function convertMoney(text: string, rows: readonly RoeRow[]): { from: string; to: string } | null {
  const m = text.trim().toLowerCase().match(/^([\d.,]+)\s*(usd|eur|vnd|vnđ)?$/);
  if (!m) return null;
  const num = Number((m[1] as string).replace(/\./g, '').replace(',', '.'));
  if (!Number.isFinite(num) || num <= 0) return null;
  const cur = (m[2] ?? 'usd').replace('vnđ', 'vnd');
  const f = (n: number) => Math.round(n).toLocaleString('vi-VN');
  if (cur === 'vnd') {
    const usd = latestRoe(rows, 'USD');
    return usd ? { from: `${f(num)} VND`, to: `${(num / usd.sell).toLocaleString('vi-VN', { maximumFractionDigits: 2 })} USD` } : null;
  }
  const r = latestRoe(rows, cur.toUpperCase());
  return r ? { from: `${f(num)} ${cur.toUpperCase()}`, to: `${f(num * r.sell)} VND` } : null;
}

export const MARGIN_ORDER: readonly MarginRule['scope'][] = ['Khách', 'Tuyến', 'Quốc gia', 'Vùng', 'Chuẩn'];
export const MARGIN_ORDER_LABEL: Record<MarginRule['scope'], string> = { Khách: 'Khách', Tuyến: 'Tuyến (cảng)', 'Quốc gia': 'Quốc gia', Vùng: 'Vùng', Chuẩn: 'Chuẩn' };

/** Margin không được thấp hơn sàn. */
export const marginOk = (m: { m20: number; m40: number; floor: number }): boolean => m.m20 >= m.floor && m.m40 >= m.floor;
